/**
 * <Button> — single source of truth for clickable affordances.
 *
 * Variants:
 * Notebook look: 1.5px ink borders, no gradients or soft shadows.
 *   primary   — marigold fill, ink text, 3px offset ink shadow that collapses
 *               on press. The one CTA per surface (modal confirm, form submit).
 *               Never two primaries on one screen.
 *   secondary — card-white with ink border. "Cancel", "Back", or the
 *               non-destructive action paired with a primary.
 *   ghost     — text-only, soft-marigold hover. Inline links, table-row
 *               actions, anything that shouldn't compete with a primary.
 *   danger    — failed-red fill with ink border. Confirm-delete, irreversible.
 *
 * Disabled (all variants): 50% opacity, no shadow. Focus ring is marigold.
 *
 * Sizes:
 *   sm — h-8 / text-xs / px-3, 10px radius.   Dense tables, inline toolbars.
 *   md — h-10 / text-sm / px-4, 14px radius.  Default — most form CTAs.
 *   lg — h-12 / text-base / px-6, 14px radius. Hero CTAs, landing.
 *
 * Polymorphism:
 *   Pass `asChild` and a single child element to render as that element with
 *   button styling — useful for <a> / <Link>. Inspired by Radix's slot pattern
 *   but kept minimal here (no Slot dep).
 *
 *   <Button asChild variant="primary">
 *     <Link to="/billing">Manage billing</Link>
 *   </Button>
 */
import React, { forwardRef } from 'react';

const VARIANTS = {
  primary:
    'bg-hl text-ink border-1.5 border-ink shadow-nb-sm font-bold ' +
    'active:translate-x-[2px] active:translate-y-[2px] active:shadow-nb-press ' +
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none',
  secondary:
    'bg-card text-ink border-1.5 border-ink ' +
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none',
  ghost:
    'bg-transparent text-ink hover:bg-hl-soft ' +
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none',
  danger:
    'bg-status-failed text-white border-1.5 border-ink ' +
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none',
};

const SIZES = {
  sm: 'h-8 px-3 text-xs gap-1.5 rounded-[10px]',
  md: 'h-10 px-4 text-sm gap-2 rounded-[14px]',
  lg: 'h-12 px-6 text-base gap-2 rounded-[14px]',
};

const BASE =
  'inline-flex items-center justify-center font-medium ' +
  'transition-[transform,box-shadow,background-color] duration-100 ' +
  'focus-ring ' +
  'whitespace-nowrap select-none cursor-pointer';

function cn(...parts) {
  return parts.filter(Boolean).join(' ');
}

const Button = forwardRef(function Button(
  {
    variant = 'primary',
    size = 'md',
    asChild = false,
    className = '',
    children,
    ...rest
  },
  ref
) {
  const classes = cn(BASE, VARIANTS[variant], SIZES[size], className);

  if (asChild) {
    // Render as the single child element, merging className.
    // Reject obvious misuse early.
    if (!React.isValidElement(children)) {
      throw new Error(
        '<Button asChild> expects exactly one valid React element as its child.'
      );
    }
    return React.cloneElement(children, {
      className: cn(classes, children.props.className),
      ref,
      ...rest,
    });
  }

  return (
    <button ref={ref} className={classes} {...rest}>
      {children}
    </button>
  );
});

export default Button;
